import React, { useState } from 'react';
import { Loader2, MessageSquareText, ChevronDown } from 'lucide-react';
import { useVickRecados } from '../../hooks/useVickRecados';
import { ROTULO_MODULO } from '../../hooks/useIaAlertas';

/**
 * Os recados escritos pelo vigia — "Resumo do dia" e "Alerta de gestão".
 *
 * Ficam ACIMA da fila porque são a leitura dos alertas, não mais um alerta: o
 * texto reúne o lote inteiro num parágrafo e diz o que aconteceu. Antes ele só
 * existia na caixa de notificação, cortado em duas linhas — e clicar na
 * notificação trazia a pessoa para uma tela onde o texto não estava em lugar
 * nenhum.
 *
 * Prosa, não dado. Por isso o desenho foge de propósito da grade densa do
 * AlertaCard: barra de acento na cor da Vick (é ela falando, não um detector),
 * linha de leitura confortável e nenhuma coluna de números. Quem bate o olho
 * tem que saber, antes de ler, que ali muda o registro.
 *
 * Fechado por padrão — quem entra em /vick pelo menu quer a fila. Abre sozinho
 * quando a pessoa chegou clicando num recado (`?notificacao=<id>`).
 */
interface Props {
  /** Recado que trouxe a pessoa até aqui, vindo de `?notificacao=` na URL. */
  destacado?: string | null;
}

const fmtData = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

const RecadosVick: React.FC<Props> = ({ destacado }) => {
  const { recados, loading, erro } = useVickRecados();
  const [aberto, setAberto] = useState(!!destacado);
  const [expandido, setExpandido] = useState<string | null>(destacado ?? null);

  // Clicar noutra notificação já estando em /vick tem que trocar o recado
  // aberto. Em render, não em efeito: efeito daria um quadro com o texto do
  // recado anterior no lugar do que a pessoa acabou de clicar.
  const [destacadoAplicado, setDestacadoAplicado] = useState(destacado ?? null);
  if ((destacado ?? null) !== destacadoAplicado) {
    setDestacadoAplicado(destacado ?? null);
    if (destacado) {
      setAberto(true);
      setExpandido(destacado);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-3 border-b border-gray-200 dark:border-slate-700 shrink-0">
        <Loader2 size={16} className="animate-spin text-gray-400" />
      </div>
    );
  }
  if (erro) {
    return (
      <p className="text-xs text-red-600 dark:text-red-400 px-4 py-2 border-b border-gray-200 dark:border-slate-700 shrink-0">
        {erro}
      </p>
    );
  }
  if (recados.length === 0) return null;

  const naoLidos = recados.filter((r) => !r.lido).length;

  return (
    <div className="border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 shrink-0">
      <button
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
      >
        <MessageSquareText size={14} className="shrink-0 text-primary dark:text-primary-light" />
        <span className="font-poppins text-xs font-semibold text-gray-800 dark:text-white">
          O que a Vick escreveu
        </span>
        <span className="text-[11px] text-gray-400">{recados.length}</span>
        {naoLidos > 0 ? (
          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-px min-w-[18px] text-center">
            {naoLidos}
          </span>
        ) : null}
        <ChevronDown
          size={14}
          className={`ml-auto shrink-0 text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`}
        />
      </button>

      {aberto ? (
        <div className="max-h-60 overflow-y-auto px-3 pb-3 space-y-1.5">
          {recados.map((r) => {
            const abertoEste = expandido === r.id;
            return (
              <div
                key={r.id}
                className={`border-l-2 rounded-r-lg transition-colors ${
                  r.id === destacadoAplicado
                    ? 'border-l-primary dark:border-l-primary-light bg-primary/5 dark:bg-primary-light/10'
                    : 'border-l-gray-200 dark:border-l-slate-600 bg-gray-50 dark:bg-slate-900/40'
                }`}
              >
                <button
                  onClick={() => setExpandido((atual) => (atual === r.id ? null : r.id))}
                  aria-expanded={abertoEste}
                  className="w-full text-left px-2.5 py-2"
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold text-gray-800 dark:text-white">{r.titulo}</span>
                    {r.modulo ? (
                      <span className="text-[10px] text-gray-500 dark:text-slate-400">
                        {ROTULO_MODULO[r.modulo]}
                      </span>
                    ) : null}
                    <span className="text-[10px] text-gray-400 dark:text-slate-500 ml-auto shrink-0">
                      {fmtData(r.criadoEm)}
                    </span>
                  </div>
                  {/* Fechado mostra o começo; aberto mostra o texto inteiro — é
                      esse texto inteiro que não existia em lugar nenhum, porque
                      a caixa de notificação corta em duas linhas. */}
                  <p
                    className={`text-[11px] text-gray-600 dark:text-slate-300 mt-1 whitespace-pre-wrap ${
                      abertoEste ? 'leading-relaxed' : 'leading-snug line-clamp-2'
                    }`}
                  >
                    {r.texto}
                  </p>
                </button>
                {abertoEste && r.alertas.length > 0 ? (
                  <p className="px-2.5 pb-2 text-[10px] text-gray-400 dark:text-slate-500">
                    Reúne {r.alertas.length} alerta(s) — a fila abaixo mostra quais continuam abertos.
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default RecadosVick;
